/**
 * Lightweight 401 event-bus.
 *
 * `api-client.ts` calls `onUnauthorized()` whenever it receives a 401 response.
 * `router.tsx` registers the concrete navigation handler via `setUnauthorizedHandler()`
 * as soon as the router is created.
 *
 * Keeping this in its own module breaks the otherwise-circular dependency chain:
 *
 *   query-client.ts  (leaf – no local imports)
 *   unauthorized-handler.ts  (leaf – no local imports)
 *   api-client.ts   ──imports──▶  unauthorized-handler.ts
 *   router.tsx      ──imports──▶  query-client.ts
 *                   ──imports──▶  unauthorized-handler.ts
 *
 * No cycles exist in this graph.
 */

type UnauthorizedHandler = () => void;

let _handler: UnauthorizedHandler = () => {
  if (import.meta.env.DEV) {
    console.warn('[UnauthorizedHandler] A 401 response was received but no handler has been registered yet.');
  }
};

/**
 * Invoke the registered unauthorized handler.
 *
 * Called by `ApiClient` when a 401 response is received.
 */
export const onUnauthorized = (): void => _handler();

/**
 * Register the function that should run when a 401 is received.
 *
 * Should be called once during app bootstrap — `router.tsx` does this
 * immediately after defining its `handleUnauthorized` function so that
 * the handler is in place before any API request can be made.
 */
export const setUnauthorizedHandler = (handler: UnauthorizedHandler): void => {
  _handler = handler;
};
